import {Record, List} from 'immutable';
import moment from 'moment-timezone';

import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import type {ProductRecordType} from 'web-app/react/plans/manage-plan/types/manage-plan-types';
import type {CreateUpdatePlanApiResponse} from 'web-app/react/plans/manage-plan/types/api-types';

import PricingGroupPrice from '../common/pricing-group-price';

const Product = Record<ProductRecordType>({
    id: '',
    productId: '',
    version: '',
    institutionId: '',
    title: '', // string
    color: null, // int
    validFrom: null, // moment
    validTo: null, // moment
    pricing: List(),
    discountable: true,
    billOnChildSick: true,
    billOnChildAbsent: true,
    billOnClosingDays: null,
    availability: List(), // [string]
    free: false,
    accountId: null,
    behaviors: new BehaviorsCollection([]),
    customFte: null, // float, number or null
    bookedPrice: null, // number or null
    planRuleId: null, // string or null
    bookingStatus: null, // BookingStatus or null
    isVirtual: false, // boolean
});

function fromAPI(data: CreateUpdatePlanApiResponse) {
    const pricing = data.pricing.map(PricingGroupPrice.fromAPI);

    return new Product({
        ...data,
        discountable: data.discountable,
        billOnClosingDays: data.billOnClosingDays ? List(data.billOnClosingDays) : null,
        availability: List(data.availability),
        validFrom: data.validFrom ? moment(data.validFrom, 'YYYY-MM-DD') : null,
        validTo: data.validTo ? moment(data.validTo, 'YYYY-MM-DD') : null,
        productId: data.id,
        id: data.version,
        pricing: List(pricing),
        behaviors: new BehaviorsCollection(data.behaviors),
        customFte: data.customFte,
        bookedPrice: data.bookedPrice,
        planRuleId: data.planRuleId,
        bookingStatus: data.bookingStatus,
        isVirtual: data.isVirtual ?? false,
    });
}

// to add types when migrating plans/manage-plan/selectors.js file
function toAPI(product) {
    const pricing = product.pricing.map(PricingGroupPrice.fromAPI);

    return {
        id: product.productId,
        discountable: product.discountable,
        billOnChildSick: product.billOnChildSick,
        billOnChildAbsent: product.billOnChildAbsent,
        billOnClosingDays: product.billOnClosingDays ?? null,
        institutionId: product.institutionId,
        title: product.title,
        color: product.color,
        validFrom: product.validFrom ? product.validFrom.format('YYYY-MM-DD') : null,
        validTo: product.validTo ? product.validTo.format('YYYY-MM-DD') : null,
        pricing: pricing.toList(),
        availability: product.availability.toList(),
        free: product.free,
        accountId: product.accountId,
        customFte: product.customFte,
        bookedPrice: product.bookedPrice,
        planRuleId: product.planRuleId,
        bookingStatus: product.bookingStatus,
        isVirtual: product.isVirtual,
    };
}

export default {
    record: Product,
    fromAPI,
    toAPI,
};
